import React, { useState } from 'react'
import { emailRegex } from '@verifier-user/verifier-cdr'
import { useApplicationContext } from '../../contexts/appContext'
import { BaseButton } from '@verifier-user/verifier-headless-ui'
import { ChevronDoubleRightIcon } from '@heroicons/react/24/solid'
import { useNavigate } from 'react-router-dom'
import RawGoogleAutoComplete from './RawGoogleAutoComplete'

export const EnergyIntro = ({
  currentErrorRef: currentErrorRef,
  setProcessing: setProcessing,
  setError: setError,
  setErrorShown: setErrorShown
}) => {
  const { login, vendorData } = useApplicationContext()
  const [showUnAuthError, setShowUnAuthError] = useState(false)

  const [email, setEmail] = useState('')

  const [address, setAddress] = useState('')
  const [isPrimaryResidence, setIsPrimaryResidence] = useState('')
  const [numberOfPeopleInHousehold, setNumberOfPeopleInHousehold] = useState(1)
  const [howManyWeekDaysIsSomeoneAtHome, setHowManyWeekDaysIsSomeoneAtHome] = useState('')
  const [timeSpentAwayFromResidence, setTimeSpentAwayFromResidence] = useState('')
  const [numberOfBedrooms, setNumberOfBedrooms] = useState(1)
  const [numberOfBathrooms, setNumberOfBathrooms] = useState(1)
  const [hasPool, setHasPool] = useState('')
  const [allowFollowUpQuestions, setAllowFollowUpQuestions] = useState('')
  const [useGasAsMainSourceOfHomeHeating, setUseGasAsMainSourceOfHomeHeating] = useState('')
  const [useGasForHotWater, setUseGasForHotWater] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [question1Error, setQuestion1Error] = useState(false)
  const [question2Error, setQuestion2Error] = useState(false)
  const [question3Error, setQuestion3Error] = useState(false)
  const [question4Error, setQuestion4Error] = useState(false)
  const [question5Error, setQuestion5Error] = useState(false)
  const [question6Error, setQuestion6Error] = useState(false)
  const [question7Error, setQuestion7Error] = useState(false)
  const [question8Error, setQuestion8Error] = useState(false)
  const [question9Error, setQuestion9Error] = useState(false)
  const [question10Error, setQuestion10Error] = useState(false)
  const [question11Error, setQuestion11Error] = useState(false)

  const googlePlacesApiKey = process.env.GOOGLE_PLACES_API_KEY

  const timeSpentAwayFromResidenceOptions = [
    {
      value: 0,
      label: 'Less than a month'
    },
    {
      value: 1,
      label: '1 - 3 months'
    },
    {
      value: 2,
      label: '3+ months'
    }
  ]

  const howManyWeekDaysIsSomeoneAtHomeOptions = [
    {
      value: 0,
      label: 'None'
    },
    {
      value: 1,
      label: '1 day'
    },
    {
      value: 2,
      label: '2 days'
    },
    {
      value: 3,
      label: '3 days'
    },
    {
      value: 4,
      label: '4 days'
    },
    {
      value: 5,
      label: '5 days'
    }
  ]

  const navigate = useNavigate()

  const validateErrors = () => {
    let valid = true
    if (!email || !emailRegex.test(email)) {
      valid = false
      setEmailError(true)
    }

    if (vendorData && vendorData?.displayQuestionnaire) {
      if (!address) {
        valid = false
        setQuestion1Error(true)
      }

      if (!isPrimaryResidence) {
        valid = false
        setQuestion2Error(true)
      }

      if (numberOfPeopleInHousehold < 1) {
        valid = false
        setQuestion3Error(true)
      }

      if (!howManyWeekDaysIsSomeoneAtHome) {
        valid = false
        setQuestion4Error(true)
      }

      if (!timeSpentAwayFromResidence) {
        valid = false
        setQuestion5Error(true)
      }

      if (numberOfBedrooms < 1) {
        valid = false
        setQuestion6Error(true)
      }

      if (numberOfBathrooms < 1) {
        valid = false
        setQuestion7Error(true)
      }

      if (!hasPool) {
        valid = false
        setQuestion8Error(true)
      }

      if (!useGasAsMainSourceOfHomeHeating) {
        valid = false
        setQuestion9Error(true)
      }

      if (!useGasForHotWater) {
        valid = false
        setQuestion10Error(true)
      }

      if (!allowFollowUpQuestions) {
        valid = false
        setQuestion11Error(true)
      }
    }

    return valid
  }

  const sendCode = (event) => {
    event.preventDefault()
    const isValid = validateErrors()

    if (isValid) {
      setProcessing(true)
      const body = { email: email }

      if (vendorData && vendorData?.displayQuestionnaire) {
        body.data = {
          address: address,
          isPrimaryResidence: isPrimaryResidence,
          numberOfPeopleInHousehold: numberOfPeopleInHousehold.toString(),
          numberOfBedrooms: numberOfBedrooms.toString(),
          numberOfBathrooms: numberOfBathrooms.toString(),
          hasPool: hasPool,
          useGasAsMainSourceOfHomeHeating: useGasAsMainSourceOfHomeHeating,
          useGasForHotWater: useGasForHotWater,
          allowFollowUpQuestions: allowFollowUpQuestions,
          howManyWeekDaysIsSomeoneAtHome: howManyWeekDaysIsSomeoneAtHomeOptions.find(
            (it) => it.value === parseInt(howManyWeekDaysIsSomeoneAtHome)
          ).label,
          timeSpentAwayFromResidence: timeSpentAwayFromResidenceOptions.find(
            (it) => it.value === parseInt(timeSpentAwayFromResidence)
          ).label
        }
      }

      login({
        body,
        onSuccess: () => {
          setProcessing(false)
          navigate('/code')
        },
        onError: (error) => {
          setProcessing(false)
          setError(error)
          const title = 'Server Unavailable'
          const message =
            "We're sorry but there has been a connection issue. Please try again, and if the issue persists, start\n" +
            '                  the process again.'

          currentErrorRef.current = {
            title: title,
            retryFunction: () => sendCode(event),
            cancelFunction: () => setErrorShown(false),
            okTitle: 'Try Again',
            cancelTitle: 'Cancel',
            errorContent: (
              <div>
                <p>{message}</p>
              </div>
            )
          }
          setErrorShown(true)
        }
      })
    }
  }

  const handleChangeEmail = (event) => {
    setShowUnAuthError(false)
    setEmail(event.target.value)
    setEmailError(false)
  }

  return (
    <div>
      <h1 data-testid='welcome-heading' className={'text-4xl sm:text-5xl mb-2 leading-tight'}>
        View your Energy Data
      </h1>
      <p>Welcome to Verifier — the privacy-friendly way to view your Energy data.</p>
      <p>
        To get started, we need your <strong>email</strong> so that you will be able to access your consent dashboard.
      </p>
      <p>
        Please enter your <strong>email</strong>
        {vendorData && vendorData?.displayQuestionnaire && (
          <span data-testid={'additional-info-text'}> and the following required information</span>
        )}
        . We'll send you a secure code you can use to login.
      </p>
      <form onSubmit={sendCode}>
        <div className='max-w-screen-lg'>
          <div className='mb-[0.5rem]'>
            <label className='block w-[80%] font-semibold' htmlFor={'email'}>
              Email address
              <span className='text-red-500 ml-1'>*</span>
            </label>
            <input
              id='email'
              type='email'
              value={email}
              onChange={handleChangeEmail}
              className={`${emailError ? 'error-input verifier-input' : 'verifier-input'} w-[60%]`}
              required
              placeholder={'Email address'}
            />
            {emailError && (
              <span data-testid='emailError' className={'text-red-600 font-bold'}>
                Please enter a valid email address.
              </span>
            )}
          </div>
          {vendorData && vendorData?.displayQuestionnaire && (
            <>
              <div className='mb-[0.5rem]'>
                <label className='block w-[80%] font-semibold' htmlFor={'address'}>
                  Q1. Can you confirm the address whose energy usage you are sharing with us?
                  <span className='text-red-500 ml-1'>*</span>
                </label>
                <RawGoogleAutoComplete
                  apiKey={googlePlacesApiKey}
                  onPlaceSelected={(place) => {
                    setQuestion1Error(false)
                    setAddress(place.formatted_address)
                  }}
                  isErrored={question1Error}
                />
                {question1Error && (
                  <span data-testid='question1Error' className={'text-red-600 font-bold'}>
                    Please enter your address.
                  </span>
                )}
              </div>
              <div className='mb-[0.5rem]'>
                <label className='block w-[80%] font-semibold' htmlFor={'primaryResidence'}>
                  Q2. Is the energy data you are sharing for your primary residence?
                  <span className='text-red-500 ml-1'>*</span>
                </label>
                <div
                  className={`mb-[0.125rem] block min-h-[1.5rem] pl-[1.5rem]`}
                  id={'primaryResidence'}
                  data-testid='primaryResidenceYes'
                >
                  <input
                    className={`radio-button ${question2Error ? 'border-red-600' : 'border-neutral-300'}`}
                    type='radio'
                    data-testid='primaryResidenceYes'
                    name='isPrimaryResidence'
                    id='isPrimaryResidenceYes'
                    value={'yes'}
                    checked={isPrimaryResidence === 'yes'}
                    onChange={(e) => {
                      setQuestion2Error(false)
                      setIsPrimaryResidence(e.target.value)
                    }}
                  />
                  <label
                    id={'labelIsPrimaryResidenceYes'}
                    className='mt-px inline-block pl-[0.15rem] hover:cursor-pointer'
                    htmlFor='primaryResidenceYes'
                  >
                    Yes
                  </label>
                </div>
                <div className={`mb-[0.125rem] block min-h-[1.5rem] pl-[1.5rem]`} data-testid='primaryResidenceNo'>
                  <input
                    className={`radio-button ${question2Error ? 'border-red-600' : 'border-neutral-300'}`}
                    type='radio'
                    data-testid='primaryResidenceNo'
                    name='isPrimaryResidence'
                    id='isPrimaryResidenceNo'
                    value={'no'}
                    checked={isPrimaryResidence === 'no'}
                    onChange={(e) => {
                      setQuestion2Error(false)
                      setIsPrimaryResidence(e.target.value)
                    }}
                  />
                  <label
                    id={'labelIsPrimaryResidenceNo'}
                    className='mt-px inline-block pl-[0.15rem] hover:cursor-pointer'
                    htmlFor='isPrimaryResidenceNo'
                  >
                    No
                  </label>
                </div>
                {question2Error && (
                  <span data-testid='question2Error' className={'text-red-600 font-bold'}>
                    Please enter Yes or No for Q2
                  </span>
                )}
              </div>
              <div className='mb-[0.5rem]'>
                <label className='block w-[80%] font-semibold' htmlFor={'numberOfPeopleInHousehold'}>
                  Q3. Including yourself, how many people are in your household?
                  <span className='text-red-500 ml-1'>*</span>
                </label>
                <input
                  id='numberOfPeopleInHousehold'
                  data-testid='numberOfPeopleInHousehold'
                  type='number'
                  value={numberOfPeopleInHousehold}
                  onChange={(e) => {
                    setQuestion3Error(false)
                    setNumberOfPeopleInHousehold(e.target.value)
                  }}
                  className={`${question3Error ? 'error-input verifier-input' : 'verifier-input'} w-[10%]`}
                  required
                />
                {question3Error && (
                  <span data-testid='question3Error' className={'text-red-600 font-bold'}>
                    Please enter a number greater than 0 for Q3
                  </span>
                )}
              </div>
              <div className='mb-[0.5rem]'>
                <label className='block w-[80%] font-semibold' htmlFor={'howManyWeekDaysIsSomeoneAtHome'}>
                  Q4. In a typical week, how many weekdays is someone at home most or all of the day?
                  <span className='text-red-500 ml-1'>*</span>
                </label>
                <div>
                  <select
                    id={`howManyWeekDaysIsSomeoneAtHome`}
                    value={howManyWeekDaysIsSomeoneAtHome}
                    onChange={(e) => {
                      setQuestion4Error(false)
                      setHowManyWeekDaysIsSomeoneAtHome(e.target.value)
                    }}
                    data-testid={`howManyWeekDaysIsSomeoneAtHome`}
                    className={`${
                      question4Error
                        ? 'error-input block w-1/4 bg-white sm:text-sm'
                        : 'block w-1/4 bg-white border border-gray-300' +
                          ' rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                    }`}
                  >
                    <option disabled hidden value='' className={'text-gray-500'}>
                      Please Select...
                    </option>
                    {howManyWeekDaysIsSomeoneAtHomeOptions.length > 0 &&
                      howManyWeekDaysIsSomeoneAtHomeOptions.map((opt, index) => (
                        <option value={opt.value} key={index}>
                          {opt.label}
                        </option>
                      ))}
                    )
                  </select>
                </div>
                {question4Error && (
                  <div data-testid='question4Error' className={'pt-2 text-red-600 font-bold'}>
                    Please choose a value from the dropdown for Q4
                  </div>
                )}
              </div>
              <div className='mb-[0.5rem]'>
                <label className='block w-[80%] font-semibold' htmlFor={'timeSpentAwayFromResidence'}>
                  Q5. In a typical year, roughly how much time would you spend away from this residence?
                  <span className='text-red-500 ml-1'>*</span>
                </label>
                <div>
                  <select
                    id={`timeSpentAwayFromResidence`}
                    value={timeSpentAwayFromResidence}
                    onChange={(e) => {
                      setQuestion5Error(false)
                      setTimeSpentAwayFromResidence(e.target.value)
                    }}
                    data-testid={`timeSpentAwayFromResidence`}
                    className={`${
                      question5Error
                        ? 'error-input block w-1/4 bg-white sm:text-sm'
                        : 'block w-1/4 bg-white border border-gray-300' +
                          ' rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'
                    }`}
                  >
                    <option disabled hidden value='' className={'text-gray-500'}>
                      Please Select...
                    </option>
                    {timeSpentAwayFromResidenceOptions.length > 0 &&
                      timeSpentAwayFromResidenceOptions.map((opt, index) => (
                        <option value={opt.value} key={index}>
                          {opt.label}
                        </option>
                      ))}
                    )
                  </select>
                </div>
                {question5Error && (
                  <div data-testid='question5Error' className={'pt-2 text-red-600 font-bold'}>
                    Please choose a value from the dropdown for Q5
                  </div>
                )}
              </div>
              <div className='mb-[0.5rem]'>
                <label className='block w-[80%] font-semibold' htmlFor={'numberOfBedrooms'}>
                  Q6. How many bedrooms are in this property?
                  <span className='text-red-500 ml-1'>*</span>
                </label>
                <input
                  id='numberOfBedrooms'
                  data-testid='numberOfBedrooms'
                  type='number'
                  value={numberOfBedrooms}
                  onChange={(e) => {
                    setQuestion6Error(false)
                    setNumberOfBedrooms(e.target.value)
                  }}
                  className={`${question6Error ? 'error-input verifier-input' : 'verifier-input'} w-[10%]`}
                  required
                />
                {question6Error && (
                  <span data-testid='question6Error' className={'text-red-600 font-bold'}>
                    Please enter a number greater than 0 for Q6
                  </span>
                )}
              </div>
              <div className='mb-[0.5rem]'>
                <label className='block w-[80%] font-semibold' htmlFor={'numberOfBathrooms'}>
                  Q7. How many bathrooms?
                  <span className='text-red-500 ml-1'>*</span>
                </label>
                <input
                  id='numberOfBathrooms'
                  data-testid='numberOfBathrooms'
                  type='number'
                  value={numberOfBathrooms}
                  onChange={(e) => {
                    setQuestion7Error(false)
                    setNumberOfBathrooms(e.target.value)
                  }}
                  className={`${question7Error ? 'error-input verifier-input' : 'verifier-input'} w-[10%]`}
                  required
                />
                {question7Error && (
                  <span data-testid='question7Error' className={'text-red-600 font-bold'}>
                    Please enter a number greater than 0 for Q7
                  </span>
                )}
              </div>
              <div className='mb-[0.5rem]'>
                <label className='block w-[80%] font-semibold' htmlFor={'doYouHaveAPool'}>
                  Q8. Do you have a pool?
                  <span className='text-red-500 ml-1'>*</span>
                </label>

                <div className={`mb-[0.125rem] block min-h-[1.5rem] pl-[1.5rem]`} id={'doYouHaveAPool'}>
                  <input
                    className={`radio-button ${question8Error ? 'border-red-600' : 'border-neutral-300'}`}
                    type='radio'
                    data-testid='poolYes'
                    name='doYouHaveAPool'
                    id='pool-yes'
                    value={'yes'}
                    checked={hasPool === 'yes'}
                    onChange={(e) => {
                      setQuestion8Error(false)
                      setHasPool(e.target.value)
                    }}
                  />
                  <label
                    id={'labelPoolYes'}
                    className='mt-px inline-block pl-[0.15rem] hover:cursor-pointer'
                    htmlFor='poolYes'
                  >
                    Yes
                  </label>
                </div>
                <div className={`mb-[0.125rem] block min-h-[1.5rem] pl-[1.5rem]`}>
                  <input
                    className={`radio-button ${question8Error ? 'border-red-600' : 'border-neutral-300'}`}
                    type='radio'
                    data-testid='pool-no'
                    name='doYouHaveAPool'
                    id='poolNo'
                    value={'no'}
                    checked={hasPool === 'no'}
                    onChange={(e) => {
                      setQuestion8Error(false)
                      setHasPool(e.target.value)
                    }}
                  />
                  <label
                    id={'labelPoolNo'}
                    className='mt-px inline-block pl-[0.15rem] hover:cursor-pointer'
                    htmlFor='poolNo'
                  >
                    No
                  </label>
                </div>
                {question8Error && (
                  <span data-testid='question8Error' className={'text-red-600 font-bold'}>
                    Please enter Yes or No for Q8
                  </span>
                )}
              </div>
              <div className='mb-[0.5rem]'>
                <label className='block w-[80%] font-semibold' htmlFor={'useGasAsMainSourceOfHomeHeatingQuestions'}>
                  Q9. Do you use your gas as the main source of home heating?
                  <span className='text-red-500 ml-1'>*</span>
                </label>

                <div
                  className={`mb-[0.125rem] block min-h-[1.5rem] pl-[1.5rem]`}
                  id={'useGasAsMainSourceOfHomeHeatingQuestions'}
                >
                  <input
                    className={`radio-button ${question9Error ? 'border-red-600' : 'border-neutral-300'}`}
                    type='radio'
                    id='useGasAsMainSourceOfHomeHeatingYes'
                    data-testid='use-gas-as-main-source-of-home-heating-yes'
                    name='useGasAsMainSourceOfHomeHeatingQuestions'
                    value={'yes'}
                    checked={useGasAsMainSourceOfHomeHeating === 'yes'}
                    onChange={(e) => {
                      setQuestion9Error(false)
                      setUseGasAsMainSourceOfHomeHeating(e.target.value)
                    }}
                  />
                  <label
                    id={'labelUseGasAsMainSourceOfHomeHeatingYes'}
                    className='mt-px inline-block pl-[0.15rem] hover:cursor-pointer'
                    htmlFor='useGasAsMainSourceOfHomeHeatingYes'
                  >
                    Yes
                  </label>
                </div>
                <div className={`mb-[0.125rem] block min-h-[1.5rem] pl-[1.5rem]`}>
                  <input
                    className={`radio-button ${question9Error ? 'border-red-600' : 'border-neutral-300'}`}
                    type='radio'
                    id='useGasAsMainSourceOfHomeHeatingNo'
                    data-testid='use-gas-as-main-source-of-home-heating-no'
                    name='useGasAsMainSourceOfHomeHeatingQuestions'
                    value={'no'}
                    checked={useGasAsMainSourceOfHomeHeating === 'no'}
                    onChange={(e) => {
                      setQuestion9Error(false)
                      setUseGasAsMainSourceOfHomeHeating(e.target.value)
                    }}
                  />
                  <label
                    id={'labelUseGasAsMainSourceOfHomeHeatingNo'}
                    className='mt-px inline-block pl-[0.15rem] hover:cursor-pointer'
                    htmlFor='useGasAsMainSourceOfHomeHeatingNo'
                  >
                    No
                  </label>
                </div>
                {question9Error && (
                  <span data-testid='question9Error' className={'text-red-600 font-bold'}>
                    Please enter Yes or No for Q9
                  </span>
                )}
              </div>
              <div className='mb-[0.5rem]'>
                <label className='block w-[80%] font-semibold' htmlFor={'useGasForHotWaterQuestions'}>
                  Q10. Do you use your gas for your hot water?
                  <span className='text-red-500 ml-1'>*</span>
                </label>

                <div className={`mb-[0.125rem] block min-h-[1.5rem] pl-[1.5rem]`} id={'useGasForHotWaterQuestions'}>
                  <input
                    className={`radio-button ${question10Error ? 'border-red-600' : 'border-neutral-300'}`}
                    type='radio'
                    id='useGasForHotWaterYes'
                    data-testid='use-gas-for-hot-water-yes'
                    name='useGasForHotWaterQuestions'
                    value={'yes'}
                    checked={useGasForHotWater === 'yes'}
                    onChange={(e) => {
                      setQuestion10Error(false)
                      setUseGasForHotWater(e.target.value)
                    }}
                  />
                  <label
                    id={'labelUseGasForHotWaterQuestionsYes'}
                    className='mt-px inline-block pl-[0.15rem] hover:cursor-pointer'
                    htmlFor='useGasForHotWaterYes'
                  >
                    Yes
                  </label>
                </div>
                <div className={`mb-[0.125rem] block min-h-[1.5rem] pl-[1.5rem]`}>
                  <input
                    className={`radio-button ${question10Error ? 'border-red-600' : 'border-neutral-300'}`}
                    type='radio'
                    id='useGasForHotWaterNo'
                    data-testid='use-gas-for-hot-water-no'
                    name='useGasForHotWaterQuestions'
                    value={'no'}
                    checked={useGasForHotWater === 'no'}
                    onChange={(e) => {
                      setQuestion10Error(false)
                      setUseGasForHotWater(e.target.value)
                    }}
                  />
                  <label
                    id={'labelUseGasForHotWaterQuestionsNo'}
                    className='mt-px inline-block pl-[0.15rem] hover:cursor-pointer'
                    htmlFor='useGasForHotWaterNo'
                  >
                    No
                  </label>
                </div>
                {question10Error && (
                  <span data-testid='question10Error' className={'text-red-600 font-bold'}>
                    Please enter Yes or No for Q10
                  </span>
                )}
              </div>
              <div>
                <label className='block w-[80%] font-semibold' htmlFor={'allowFollowUpQuestions'}>
                  Q11. Are you OK for us to email you to ask you more questions if we find we need more detail to
                  develop our household rating score?
                  <span className='text-red-500 ml-1'>*</span>
                </label>

                <div className={`mb-[0.125rem] block min-h-[1.5rem] pl-[1.5rem]`} id={'allowFollowUpQuestions'}>
                  <input
                    className={`radio-button ${question11Error ? 'border-red-600' : 'border-neutral-300'}`}
                    type='radio'
                    id='allowFollowUpQuestionsYes'
                    data-testid='allow-follow-up-questions-yes'
                    name='allowFollowUpQuestions'
                    value={'yes'}
                    checked={allowFollowUpQuestions === 'yes'}
                    onChange={(e) => {
                      setQuestion11Error(false)
                      setAllowFollowUpQuestions(e.target.value)
                    }}
                  />
                  <label
                    id={'labelAllowFollowUpQuestionsYes'}
                    className='mt-px inline-block pl-[0.15rem] hover:cursor-pointer'
                    htmlFor='allowFollowUpQuestionsYes'
                  >
                    Yes
                  </label>
                </div>
                <div className={`mb-[0.125rem] block min-h-[1.5rem] pl-[1.5rem]`}>
                  <input
                    className={`radio-button ${question11Error ? 'border-red-600' : 'border-neutral-300'}`}
                    type='radio'
                    id='allowFollowUpQuestionsNo'
                    data-testid='allow-follow-up-questions-no'
                    name='allowFollowUpQuestions'
                    value={'no'}
                    checked={allowFollowUpQuestions === 'no'}
                    onChange={(e) => {
                      setQuestion11Error(false)
                      setAllowFollowUpQuestions(e.target.value)
                    }}
                  />
                  <label
                    id={'labelAllowFollowUpQuestionsNo'}
                    className='mt-px inline-block pl-[0.15rem] hover:cursor-pointer'
                    htmlFor='allowFollowUpQuestionsNo'
                  >
                    No
                  </label>
                </div>
                {question11Error && (
                  <span data-testid='question11Error' className={'text-red-600 font-bold'}>
                    Please enter Yes or No for Q11
                  </span>
                )}
              </div>
            </>
          )}

          {showUnAuthError && (
            <p className='verifier-error-text text-red-600 mt-2 mb-0' data-testid='un-auth-error'>
              {showUnAuthError ? `The 'email address' does not exist` : "Sorry that didn't work. Please try again"}
            </p>
          )}
        </div>

        <BaseButton
          type='submit'
          data-testid='btn-send-code'
          className='mb-4 mt-4'
          onClick={sendCode}
          id='submit-button'
        >
          Send me a code
          <ChevronDoubleRightIcon className='w-4 h-4 inline' />
        </BaseButton>
      </form>
      {/*<CommonIntro />*/}
    </div>
  )
}
